import "pages/StandardTreatment/shared/masterData.scss";
import "pages/MasterPengobatanStandar/index.scss";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import {
  FiArchive,
  FiCheckSquare,
  FiCopy,
  FiEye,
  FiList,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiRotateCcw,
  FiSend,
  FiSettings,
  FiTrash2,
} from "react-icons/fi";
import Button from "components/atoms/Button";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import useServiceData from "../shared/useServiceData";
import useBootstrapModal from "../shared/useBootstrapModal";
import ReleaseModal, { RELEASE_MODAL_ID } from "../shared/ReleaseModal";
import { ActivePill, ErrorState, VersionStatusPill, formatDateTime } from "../shared/ui";

const LIMIT = 10;
const STATUS_FILTERS = [
  { label: "Semua status", value: "" },
  { label: "DRAFT", value: "DRAFT" },
  { label: "FINAL", value: "FINAL" },
  { label: "ARCHIVE", value: "ARCHIVE" },
];

export const configUrl = (versionId, step) =>
  `/master-pengobatan-standar?version_id=${versionId}${step ? `&step=${step}` : ""}`;
export const rulesUrl = (versionId) => `/standard-treatment/rules?version_id=${versionId}`;

const Versions = () => {
  const history = useHistory();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [releasePage, setReleasePage] = useState(1);
  const [busy, setBusy] = useState(false);
  const release = useBootstrapModal(RELEASE_MODAL_ID);

  useEffect(() => {
    document.title = "SISMAL | Daftar Versi";
  }, []);

  // 404 = belum pernah ada rilis di environment ini (HANDOFF §6.0 Step 1).
  const active = useServiceData(() => standardTreatmentService.getActiveRelease(), []);
  const activeVersionId = active.data?.version_id;
  const versions = useServiceData(
    () => standardTreatmentService.listVersions({ page, limit: LIMIT, status: status || undefined }),
    [page, status]
  );
  const releases = useServiceData(
    () => standardTreatmentService.listReleases({ page: releasePage, limit: LIMIT }),
    [releasePage]
  );
  const refreshAll = () => Promise.all([active.reload(), versions.reload(), releases.reload()]);

  const run = (promise, success, after) => {
    setBusy(true);
    return promise
      .then((result) => {
        toast.success(success(result));
        return refreshAll().then(() => after && after(result));
      })
      .catch((err) => toast.error(errorMessages(err).join(" · ")))
      .finally(() => setBusy(false));
  };

  const cloneVersion = (v) =>
    run(
      standardTreatmentService.cloneVersion(v.id),
      (r) => `Versi ${v.version} diklon menjadi Draft Versi ${r.version} (${r.rule_count} rule)`,
      (r) => history.push(configUrl(r.id))
    );

  const confirmAction = (title, message, label, action) =>
    confirmAlert({ title, message, buttons: [{ label, onClick: action }, { label: "Batal" }] });

  // Tombol aksi bergaya ActionButton (atoms) — varian: primary | info | warning | danger.
  const actionsFor = (v) => {
    const isActive = v.id === activeVersionId;
    const content = (Icon, label) => (
      <>
        <Icon size={14} aria-hidden="true" />
        <span>{label}</span>
      </>
    );
    const link = (Icon, label, to, variant = "info") => (
      <Link key={label} className={`st-action is-${variant}`} to={to}>
        {content(Icon, label)}
      </Link>
    );
    const button = (Icon, label, onClick, variant, props = {}) => (
      <button
        key={label}
        type="button"
        className={`st-action is-${variant}`}
        onClick={onClick}
        disabled={busy || props.disabled}
        title={props.title}
      >
        {content(Icon, label)}
      </button>
    );
    if (v.status === "DRAFT")
      return [
        link(FiSettings, "Konfigurasi", configUrl(v.id), "primary"),
        link(FiList, "Rules", rulesUrl(v.id)),
        link(FiCheckSquare, "Validasi", configUrl(v.id, 4)),
        link(FiPlay, "Simulasi", configUrl(v.id, 5)),
        button(FiSend, "Rilis", () => release.open("PUBLISH", v), "primary"),
        button(FiTrash2, "Hapus", () => confirmAction(
          `Hapus Draft Versi ${v.version}?`,
          "Draft beserta seluruh Rule, Kondisi, dan Terapinya dihapus. Nomor versi tidak dipakai ulang.",
          "Ya, hapus",
          () => run(standardTreatmentService.deleteVersion(v.id), () => `Draft Versi ${v.version} dihapus`)
        ), "danger"),
      ];
    // Simulasi tersedia untuk semua status: Draft dikompilasi saat itu juga,
    // Final/Archive membaca snapshot rilisnya as-is (simulation.service.js).
    const common = [
      link(FiEye, "Lihat", configUrl(v.id), "primary"),
      link(FiList, "Rules", rulesUrl(v.id)),
      link(FiPlay, "Simulasi", configUrl(v.id, 5)),
      button(FiCopy, "Klon", () => cloneVersion(v), "info"),
    ];
    if (v.status === "FINAL")
      return [
        ...common,
        !isActive && button(FiRotateCcw, "Rollback", () => release.open("ROLLBACK", v), "warning"),
        button(FiArchive, "Archive", () => confirmAction(
          `Arsipkan Versi ${v.version}?`,
          "Versi arsip tetap beku dan tidak ditawarkan sebagai tujuan rollback. Dapat dibatalkan dengan Unarchive.",
          "Ya, arsipkan",
          () => run(standardTreatmentService.archiveVersion(v.id), () => `Versi ${v.version} diarsipkan`)
        ), "warning", { disabled: isActive, title: isActive ? "Versi aktif tidak dapat diarsipkan" : undefined }),
      ].filter(Boolean);
    return [
      ...common,
      button(FiRefreshCw, "Unarchive", () => confirmAction(
        `Batalkan arsip Versi ${v.version}?`,
        "Versi kembali berstatus FINAL dan dapat menjadi tujuan rollback.",
        "Ya, kembalikan",
        () => run(standardTreatmentService.unarchiveVersion(v.id), () => `Versi ${v.version} kembali FINAL`)
      ), "warning"),
    ];
  };

  const versionColumns = [
    {
      header: "Versi",
      size: 140,
      cell: ({ row }) => (
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 6 }}>
          <strong>Versi {row.original.version}</strong>
          {row.original.id === activeVersionId && <ActivePill />}
        </div>
      ),
    },
    { header: "Status", size: 100, cell: ({ row }) => <VersionStatusPill status={row.original.status} /> },
    {
      header: "Asal",
      size: 150,
      cell: ({ row }) => (row.original.clone_from_version ? `Klon dari Versi ${row.original.clone_from_version}` : "Draft kosong / seed"),
    },
    { header: "Dibuat", size: 150, cell: ({ row }) => formatDateTime(row.original.createdAt) },
    { header: "Diperbarui", size: 150, cell: ({ row }) => formatDateTime(row.original.updatedAt) },
    { header: "Aksi", size: 380, cell: ({ row }) => <div className="row-actions">{actionsFor(row.original)}</div> },
  ];
  const versionTable = useReactTable({
    data: versions.data?.version_rules || [],
    columns: versionColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Nomor versi untuk riwayat release (Release hanya membawa version_id).
  const versionNumbers = useServiceData(() => standardTreatmentService.listVersions({ page: 1, limit: 100 }), [releases.data]);
  const numberOf = (id) => versionNumbers.data?.version_rules.find((v) => v.id === id)?.version ?? "—";

  const releaseColumns = useMemo(
    () => [
      { header: "No. Rilis", size: 90, cell: ({ row }) => <strong>#{row.original.release_no}</strong> },
      { header: "Versi", size: 90, cell: ({ row }) => `Versi ${numberOf(row.original.version_id)}` },
      {
        header: "Tipe",
        size: 110,
        cell: ({ row }) => (
          <span className={`mps-pill ${row.original.release_type === "PUBLISH" ? "is-success" : "is-warning"}`}>
            {row.original.release_type}
          </span>
        ),
      },
      { header: "Dirilis", size: 150, cell: ({ row }) => formatDateTime(row.original.released_at) },
      {
        header: "Oleh",
        size: 120,
        cell: ({ row }) =>
          row.original.released_by ? <code className="md-code" title={row.original.released_by}>{row.original.released_by.slice(0, 8)}…</code> : "—",
      },
      { header: "Catatan", size: 220, cell: ({ row }) => row.original.change_note || "—" },
    ],
    [versionNumbers.data]
  );
  const releaseTable = useReactTable({
    data: releases.data?.version_releases || [],
    columns: releaseColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const activeNotFound = active.status === 404;

  return (
    <div className="master-data-page">
      <h1 className="page-title">Daftar Versi</h1>
      <p className="page-subtitle">
        Versi = satu set lengkap Rule, hanya bernomor (tanpa nama). Versi aktif adalah versi pada
        release terbaru — bukan sekadar berstatus FINAL.
      </p>

      <section className="mps-subcard mb-3">
        <div className="action-wrapper mb-0">
          <div>
            <p className="mps-muted mb-1">Versi aktif</p>
            {active.loading && !active.data ? (
              <p className="mb-0">Memuat...</p>
            ) : activeNotFound ? (
              <p className="mb-0">Belum ada versi yang pernah dirilis.</p>
            ) : active.error ? (
              <p className="mb-0 text-danger">{active.error.join(", ")}</p>
            ) : (
              <p className="mb-0">
                <strong>Versi {active.data.version}</strong> <ActivePill />{" "}
                <span className="mps-muted">
                  · rilis #{active.data.release_no} ({active.data.release_type}) · {formatDateTime(active.data.released_at)}
                </span>
              </p>
            )}
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              size="normal"
              isDisabled={!activeVersionId || busy}
              onClick={() => cloneVersion({ id: activeVersionId, version: active.data.version })}
            >
              <FiCopy size={16} className="mr-2" />
              Klon versi aktif → Draft
            </Button>
            <Button
              variant="secondary"
              size="normal"
              isDisabled={busy}
              onClick={() =>
                confirmAction(
                  "Buat Draft kosong?",
                  "Draft kosong hanya untuk kebijakan pertama. Alur normal adalah mengklon versi aktif.",
                  "Ya, buat",
                  () => run(standardTreatmentService.createVersion(), (r) => `Draft Versi ${r.version} dibuat`, (r) => history.push(configUrl(r.id)))
                )
              }
            >
              <FiPlus size={16} className="mr-2" />
              Draft kosong
            </Button>
          </div>
        </div>
      </section>

      <div className="action-wrapper">
        <h2 className="md-section-title mt-0">Versi</h2>
        <div className="action-status">
          <InputSelect
            isClearable={false}
            aria-label="Filter status"
            data={STATUS_FILTERS}
            value={STATUS_FILTERS.find((o) => o.value === status)}
            onChange={(sel) => {
              setStatus(sel?.value ?? "");
              setPage(1);
            }}
          />
        </div>
      </div>
      {versions.error ? (
        <ErrorState messages={versions.error} onRetry={versions.reload} />
      ) : (
        <TableDataComponent
          columnLength={versionColumns.length}
          tableInstance={versionTable}
          onPageChange={(next) => next && setPage(next)}
          loading={versions.loading}
          currentPage={versions.data?.currentPage ?? page}
          limit={LIMIT}
          nextPage={versions.data?.nextPage}
          previousPage={versions.data?.previousPage}
          total={versions.data?.total ?? 0}
        />
      )}

      <h2 className="md-section-title">Riwayat Rilis</h2>
      <p className="page-subtitle mt-1 mb-0">
        Log tidak dapat diubah atau dihapus: setiap rilis dan rollback menambah baris baru; riwayat tidak pernah diubah.
      </p>
      {releases.error ? (
        <div className="mt-3">
          <ErrorState messages={releases.error} onRetry={releases.reload} />
        </div>
      ) : (
        <TableDataComponent
          columnLength={releaseColumns.length}
          tableInstance={releaseTable}
          onPageChange={(next) => next && setReleasePage(next)}
          loading={releases.loading}
          currentPage={releases.data?.currentPage ?? releasePage}
          limit={LIMIT}
          nextPage={releases.data?.nextPage}
          previousPage={releases.data?.previousPage}
          total={releases.data?.total ?? 0}
        />
      )}

      <ReleaseModal
        key={release.modal.openCount}
        version={release.modal.item}
        releaseType={release.modal.mode}
        onReleased={(result) => {
          release.close();
          toast.success(`Rilis #${result.release_no}: Versi ${result.version} kini aktif`);
          refreshAll();
        }}
      />
    </div>
  );
};

export default Versions;
